import { Button, ButtonDropdown, Tooltip } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { type FC } from 'react';
import { roofBearingLabel } from './roofLabels';
import { type FaceSummary } from './types';

export type InspectorDestination =
  | 'layout'
  | 'mounting'
  | 'alignment'
  | 'reference'
  | 'spacing'
  | 'inverter'
  | 'tariff'
  | 'equipment'
  | 'electrical';
export type InspectorFocusRequest = {
  destination: InspectorDestination;
  sequence: number;
};

const InspectorNavigation: FC<{
  faces: FaceSummary[];
  selectedFaceId: number | null;
  excludedFaceIds: number[];
  rotating: boolean;
  targetLocked?: boolean;
  onSelectFace: (id: number | null) => void;
  selectedScopeLabel?: string | null;
}> = ({
  faces,
  selectedFaceId,
  excludedFaceIds,
  rotating,
  targetLocked = false,
  onSelectFace,
  selectedScopeLabel,
}) => {
  const index = faces.findIndex((face) => face.faceId === selectedFaceId);
  const label = (face: FaceSummary, index: number) =>
    `Roof ${index + 1} · ${roofBearingLabel(face)}`;
  const blocked =
    'Apply or cancel the rotation before changing roofs or layout settings.';
  return (
    <nav aria-label="Design shortcuts" className="min-w-0">
      {faces.length > 0 && (
        <div className="flex min-w-0 items-center gap-1">
          <Tooltip
            className="min-w-0 flex-1"
            tooltipContent={
              rotating ? blocked : (selectedScopeLabel ?? 'Choose a roof')
            }
          >
            <ButtonDropdown
              variant="secondary"
              size="sm"
              className="min-h-11 min-w-0 w-full justify-between gap-1 px-2 py-1 sm:min-h-0"
              aria-label="Choose roof"
              disabled={rotating || targetLocked}
              dropdownOptions={{
                items: [
                  {
                    label: 'All roofs',
                    selected: selectedFaceId == null,
                    onClick: () => onSelectFace(null),
                  },
                  ...faces.map((face, index) => ({
                    label: `${label(face, index)} · ${face.panelsIncluded} panels${excludedFaceIds.includes(face.faceId) ? ' · excluded' : ''}`,
                    selected: face.faceId === selectedFaceId,
                    onClick: () => onSelectFace(face.faceId),
                  })),
                ],
              }}
            >
              <span
                className="line-clamp-2 min-w-0 flex-1 text-left leading-tight"
                aria-live="polite"
              >
                {index < 0 && `All ${faces.length} roofs`}
                {index >= 0 && selectedScopeLabel && (
                  <>
                    <span aria-hidden>
                      {selectedScopeLabel
                        .replace(' panel group', ' · group')
                        .replace(' panels selected', ' panels')
                        .replace(' panel selected', ' panel')}
                    </span>
                    <span className="sr-only">{selectedScopeLabel}</span>
                  </>
                )}
                {index >= 0 &&
                  !selectedScopeLabel &&
                  label(faces[index], index)}
              </span>
              <UntitledIcon
                name="chevron-down"
                className="size-4 shrink-0"
                aria-hidden
              />
            </ButtonDropdown>
          </Tooltip>
          <Tooltip tooltipContent={rotating ? blocked : 'Previous roof'}>
            <Button
              className="min-h-11 min-w-11 shrink-0 justify-center p-0 sm:size-8 sm:min-h-0 sm:min-w-0"
              variant="quaternary"
              size="sm"
              aria-label="Previous roof"
              disabled={rotating || targetLocked || index <= 0}
              onClick={() => onSelectFace(faces[index - 1].faceId)}
            >
              <UntitledIcon name="chevron-left" className="size-4" />
            </Button>
          </Tooltip>
          <Tooltip tooltipContent={rotating ? blocked : 'Next roof'}>
            <Button
              className="min-h-11 min-w-11 shrink-0 justify-center p-0 sm:size-8 sm:min-h-0 sm:min-w-0"
              variant="quaternary"
              size="sm"
              aria-label="Next roof"
              disabled={rotating || targetLocked || index === faces.length - 1}
              onClick={() => onSelectFace(faces[index + 1].faceId)}
            >
              <UntitledIcon name="chevron-right" className="size-4" />
            </Button>
          </Tooltip>
        </div>
      )}
    </nav>
  );
};
export default InspectorNavigation;
